import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
// import { useCallback } from 'react';
// import { useSelector } from 'react-redux';

import commentsService from './commentsService';
import {
    GroupComment,
    CommentInput,
    CommentType,
    CommentsResponse,
    CreateCommentResponse
} from '@cobu/types';
import { analyticsService, AnalyticsEvent } from '@cobu/analytics';
// import { showErrorMessage } from '@/utils/flashMessage';

// Query keys for comments
export const COMMENTS_KEYS = {
    all: ['comments'],
    lists: () => [...COMMENTS_KEYS.all, 'list'],
    list: (groupId?: string, groupEventId?: string, buildingId?: string) =>
        [...COMMENTS_KEYS.lists(), { groupId, groupEventId, buildingId }],
    listWithDate: (groupId?: string, groupEventId?: string, buildingId?: string, eventDate?: string) =>
        [...COMMENTS_KEYS.lists(), { groupId, groupEventId, buildingId, eventDate }],
    details: () => [...COMMENTS_KEYS.all, 'detail'],
    detail: (id: string) => [...COMMENTS_KEYS.details(), id],
    pinned: (groupId?: string, buildingId?: string) =>
        [...COMMENTS_KEYS.all, 'pinned', { groupId, buildingId }],
    reactions: (commentId: string) => [...COMMENTS_KEYS.all, 'reactions', commentId],
    search: (query: string, groupId?: string, groupEventId?: string) =>
        [...COMMENTS_KEYS.all, 'search', { query, groupId, groupEventId }],
};

/**
 * Hook to fetch comments with pagination
 */
export const useCommentsQuery = (
    groupId?: string,
    groupEventId?: string,
    buildingId?: string,
    eventDate?: string,
    enabled: boolean = true
) => {
    return useInfiniteQuery<CommentsResponse, Error>({
        queryKey: eventDate
            ? COMMENTS_KEYS.listWithDate(groupId, groupEventId, buildingId, eventDate)
            : COMMENTS_KEYS.list(groupId, groupEventId, buildingId),
        queryFn: async ({ pageParam = 1 }) => {
            const page = pageParam as number;
            if (buildingId && !groupId && !groupEventId) {
                return commentsService.getBuildingChatComments(buildingId, page);
            }
            if (groupEventId && !groupId && !buildingId) {
                return commentsService.getEventChatComments(groupEventId, page, 20, eventDate);
            }
            return commentsService.getComments(groupId, groupEventId, page, 20, eventDate);
        },
        getNextPageParam: (lastPage: CommentsResponse) =>
            lastPage.hasMore ? lastPage.page + 1 : undefined,
        initialPageParam: 1,
        enabled: enabled && !!(groupId || groupEventId || buildingId),
        staleTime: 2 * 60 * 1000, // 2 minutes
        gcTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: false,
    });
};

/**
 * Hook to fetch pinned comments
 */
export const usePinnedCommentsQuery = (
    buildingId?: string,
    groupId?: string,
    groupEventId?: string,
    enabled: boolean = true
) => {
    return useQuery({
        queryKey: COMMENTS_KEYS.pinned(groupId, buildingId),
        queryFn: () => commentsService.getPinnedComments(buildingId, groupId),
        enabled: enabled && !groupId && !groupEventId && !!buildingId,
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
    });
};

/**
 * Hook to fetch comment reactions
 */
export const useCommentReactionsQuery = (
    commentId: string,
    commentType: CommentType = CommentType.GroupComment,
    enabled: boolean = true
) => {
    return useQuery({
        queryKey: COMMENTS_KEYS.reactions(commentId),
        queryFn: () => commentsService.getCommentReactions(commentId, commentType),
        enabled: enabled && !!commentId,
        staleTime: 1 * 60 * 1000, // 1 minute
        gcTime: 5 * 60 * 1000, // 5 minutes
    });
};

/**
 * Hook to search comments
 */
export const useSearchCommentsQuery = (
    query: string,
    groupId?: string,
    groupEventId?: string,
    enabled: boolean = true
) => {
    return useInfiniteQuery<CommentsResponse, Error>({
        queryKey: COMMENTS_KEYS.search(query, groupId, groupEventId),
        queryFn: async ({ pageParam = 1 }) => {
            const page = pageParam as number;
            return commentsService.searchComments(query, groupId, groupEventId, page);
        },
        getNextPageParam: (lastPage: CommentsResponse) =>
            lastPage.hasMore ? lastPage.page + 1 : undefined,
        initialPageParam: 1,
        enabled: enabled && !!query.trim(),
        staleTime: 30 * 1000, // 30 seconds
        gcTime: 2 * 60 * 1000, // 2 minutes
    });
};

/**
 * Hook to create a comment
 */
export const useCreateCommentMutation = (
    commentType: CommentType = CommentType.GroupComment,
    groupId?: string,
    groupEventId?: string,
    buildingId?: string,
    eventDate?: string
) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: CommentInput): Promise<CreateCommentResponse> =>
            commentsService.createComment(input, commentType),
        onSuccess: (response, input) => {
            //   analyticsService.trackEvent(AnalyticsEvent.CREATE_COMMENT, {
            //     response,
            //   });
            // Invalidate and refetch comments list(s)
            if (groupEventId && eventDate && !groupId && !buildingId) {
                // Event chats that are scoped by date use a different query key
                queryClient.invalidateQueries({
                    queryKey: COMMENTS_KEYS.listWithDate(groupId, groupEventId, buildingId, eventDate),
                });
            } else {
                queryClient.invalidateQueries({
                    queryKey: COMMENTS_KEYS.list(groupId, groupEventId, buildingId),
                });
            }

            const pinnedComments = queryClient.getQueryData(COMMENTS_KEYS.pinned(groupId, buildingId)) as GroupComment[];

            const isParentCommentPinned = pinnedComments?.some((comment: GroupComment) => comment.uuid === input.parent);

            // Invalidate pinned comments if this might be pinned
            if (response?.comment?.pinComment || isParentCommentPinned) {
                queryClient.invalidateQueries({
                    queryKey: COMMENTS_KEYS.pinned(groupId, buildingId)
                });
            }

            // Track analytics
            //   analyticsService.trackEvent(AnalyticsEvent.CREATE_COMMENT, {
            //     commentType,
            //     groupId,
            //     groupEventId,
            //     buildingId,
            //     isReply: !!input.parent,
            //     hasImages: !!(input.images && input.images.length > 0),
            //     contentLength: input.contents.length,
            //     moderationType: response.moderationType,
            //   });
        },
        onError: (error) => {
            //   analyticsService.trackEvent(AnalyticsEvent.CREATE_COMMENT_FAILURE, {
            //     commentType,
            //     groupId,
            //     groupEventId,
            //     error: error.message,
            //   });
            //   showErrorMessage(error.message);
        },
    });
};

/**
 * Hook to update a comment
 */
export const useUpdateCommentMutation = (
    commentType: CommentType = CommentType.GroupComment,
    groupId?: string,
    groupEventId?: string,
    buildingId?: string
) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ commentId, updates }: { commentId: string, updates: Partial<CommentInput> }) =>
            commentsService.updateComment(commentId, updates, commentType),
        onSuccess: (updatedComment) => {
            // Update the comment in cache
            queryClient.setQueryData(
                COMMENTS_KEYS.list(groupId, groupEventId, buildingId),
                (oldData: any) => {
                    if (!oldData) return oldData;

                    return {
                        ...oldData,
                        pages: oldData.pages.map((page: CommentsResponse) => ({
                            ...page,
                            comments: page.comments.map((comment: GroupComment) =>
                                comment.uuid === updatedComment.uuid ? updatedComment : comment
                            ),
                        })),
                    };
                }
            );

            // Track analytics
            analyticsService.trackEvent(AnalyticsEvent.UPDATE_COMMENT, {
              commentId: updatedComment.uuid,
              commentType,
              groupId,
              groupEventId,
            });
        },
        onError: (error) => {
            console.error('Failed to update comment:', error);
        },
    });
};

/**
 * Hook to delete a comment
 */
export const useDeleteCommentMutation = (
    commentType: CommentType = CommentType.GroupComment,
    groupId?: string,
    groupEventId?: string,
    buildingId?: string
) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (commentId: string) =>
            commentsService.deleteComment(commentId, commentType),
        onSuccess: (_, commentId) => {
            // Remove the comment from cache
            queryClient.setQueryData(
                COMMENTS_KEYS.list(groupId, groupEventId, buildingId),
                (oldData: any) => {
                    if (!oldData) return oldData;

                    return {
                        ...oldData,
                        pages: oldData.pages.map((page: CommentsResponse) => ({
                            ...page,
                            comments: page.comments.map((comment: GroupComment) => {
                                // 1) Parent comment: soft-delete
                                if (comment.uuid === commentId) {
                                    return {
                                        ...comment,
                                        removed: true,
                                        removedBy: 'USER',
                                    } as GroupComment;
                                }

                                // 2) Child reply: soft-delete within one level of children
                                if (Array.isArray(comment.children) && comment.children.some(c => c.uuid === commentId)) {
                                    return {
                                        ...comment,
                                        children: comment.children.map(c =>
                                            c.uuid === commentId
                                                ? ({ ...c, removed: true, removedBy: 'USER' } as GroupComment)
                                                : c
                                        ),
                                    } as GroupComment;
                                }

                                // unchanged
                                return comment;
                            }),
                        })),
                    };
                }
            );

            // Invalidate pinned comments in case a pinned comment was deleted
            queryClient.invalidateQueries({
                queryKey: COMMENTS_KEYS.pinned(groupId, buildingId)
            });

            // Track analytics
            analyticsService.trackEvent(AnalyticsEvent.DELETE_COMMENT, {
              commentId,
              commentType,
              groupId,
              groupEventId,
            });
        },
        onError: (error) => {
            console.error('Failed to delete comment:', error);
        },
    });
};

/**
 * Hook to react to a comment
 */
export const useReactToCommentMutation = (
  commentType: CommentType = CommentType.GroupComment,
  groupId?: string,
  groupEventId?: string,
  buildingId?: string,
  eventDate?: string,
  currentUserId?: string
) => {
  const queryClient = useQueryClient();

  // Resolve the query key to update depending on whether we have an eventDate
  const resolveCommentsKey = (isPinned: boolean) =>
    isPinned
      ? COMMENTS_KEYS.pinned(groupId, buildingId)
      : eventDate
        ? COMMENTS_KEYS.listWithDate(groupId, groupEventId, buildingId, eventDate)
        : COMMENTS_KEYS.list(groupId, groupEventId, buildingId);

  return useMutation({
    mutationFn: ({
      commentId,
      reactionName,
      isRemoving = false,
      parentCommentId,
      isPinned = false
    }: {
      commentId: string;
      reactionName: string | null;
      isRemoving?: boolean;
      parentCommentId?: string;
      isPinned: boolean;
    }) =>
      commentsService.reactToComment(commentId, reactionName, isRemoving, commentType, parentCommentId),
    onMutate: async ({ commentId, reactionName, isRemoving, isPinned }) => {
      // Optimistic update
      const commentsKey = resolveCommentsKey(isPinned);
      await queryClient.cancelQueries({
        queryKey: commentsKey
      });

      const previousData = queryClient.getQueryData(commentsKey);

      // Update the comment's reaction data optimistically
      if (isPinned) {
        // Pinned comments cache shape: GroupComment[]
        queryClient.setQueryData(
          commentsKey,
          (oldData: any) => {
            if (!Array.isArray(oldData)) return oldData;

            return oldData.map((comment: GroupComment) => {
              // Case 1: reacting to a parent comment
              if (comment.uuid === commentId) {
                const updatedComment = { ...comment } as GroupComment;

                if (isRemoving) {
                  updatedComment.myReaction = undefined;
                  updatedComment.reactionsCount = Math.max(0, updatedComment.reactionsCount - 1);
                  try {
                    const userId = currentUserId;
                    if (userId && comment.myReaction) {
                      const current = { ...(updatedComment.byReactionUserIds || {}) } as Record<string, string[]>;
                      const prevKey = comment.myReaction;
                      const prevArr = Array.isArray(current[prevKey]) ? current[prevKey] : [];
                      current[prevKey] = prevArr.filter((id: string) => id !== userId);
                      updatedComment.byReactionUserIds = current;
                    }
                  } catch { }
                } else {
                  updatedComment.myReaction = reactionName || undefined;
                  if (!comment.myReaction) {
                    updatedComment.reactionsCount = updatedComment.reactionsCount + 1;
                  }
                  try {
                    const userId = currentUserId;
                    if (userId && reactionName) {
                      const current = { ...(updatedComment.byReactionUserIds || {}) } as Record<string, string[]>;
                      if (comment.myReaction && comment.myReaction !== reactionName) {
                        const prevArr = Array.isArray(current[comment.myReaction]) ? current[comment.myReaction] : [];
                        current[comment.myReaction] = prevArr.filter((id: string) => id !== userId);
                      }
                      const newArr = Array.isArray(current[reactionName]) ? current[reactionName] : [];
                      if (!newArr.includes(userId)) {
                        current[reactionName] = [...newArr, userId];
                      } else {
                        current[reactionName] = newArr;
                      }
                      updatedComment.byReactionUserIds = current;
                    }
                  } catch { }
                }

                return updatedComment;
              }

              // Case 2: reacting to a child reply
              const hasMatchingChild = Array.isArray(comment.children) && comment.children.some((child) => child.uuid === commentId);
              if (hasMatchingChild) {
                const updatedParent = { ...comment } as GroupComment;
                updatedParent.children = (comment.children || []).map((child) => {
                  if (child.uuid !== commentId) return child;

                  const updatedChild = { ...child } as GroupComment;
                  if (isRemoving) {
                    updatedChild.myReaction = undefined;
                    updatedChild.reactionsCount = Math.max(0, (updatedChild.reactionsCount || 0) - 1);
                    try {
                      const userId = currentUserId;
                      const prevKey = child.myReaction;
                      if (userId && prevKey) {
                        const current = { ...(updatedChild.byReactionUserIds || {}) } as Record<string, string[]>;
                        const prevArr = Array.isArray(current[prevKey]) ? current[prevKey] : [];
                        current[prevKey] = prevArr.filter((id: string) => id !== userId);
                        updatedChild.byReactionUserIds = current;
                      }
                    } catch { }
                  } else {
                    updatedChild.myReaction = reactionName || undefined;
                    if (!child.myReaction) {
                      updatedChild.reactionsCount = (updatedChild.reactionsCount || 0) + 1;
                    }
                    try {
                      const userId = currentUserId;
                      if (userId && reactionName) {
                        const current = { ...(updatedChild.byReactionUserIds || {}) } as Record<string, string[]>;
                        if (child.myReaction && child.myReaction !== reactionName) {
                          const prevArr = Array.isArray(current[child.myReaction]) ? current[child.myReaction] : [];
                          current[child.myReaction] = prevArr.filter((id: string) => id !== userId);
                        }
                        const newArr = Array.isArray(current[reactionName]) ? current[reactionName] : [];
                        if (!newArr.includes(userId)) {
                          current[reactionName] = [...newArr, userId];
                        } else {
                          current[reactionName] = newArr;
                        }
                        updatedChild.byReactionUserIds = current;
                      }
                    } catch { }
                  }

                  return updatedChild;
                });
                return updatedParent;
              }

              // No changes
              return comment;
            });
          }
        );
      } else {
        // Paginated comments cache shape: { pages: [...], ... }
        queryClient.setQueryData(
          commentsKey,
          (oldData: any) => {
            if (!oldData) return oldData;

            return {
              ...oldData,
              pages: oldData.pages.map((page: CommentsResponse) => ({
                ...page,
                comments: page.comments.map((comment: GroupComment) => {
                  // Case 1: reacting to a parent comment
                  if (comment.uuid === commentId) {
                    const updatedComment = { ...comment } as GroupComment;

                    if (isRemoving) {
                      updatedComment.myReaction = undefined;
                      updatedComment.reactionsCount = Math.max(0, updatedComment.reactionsCount - 1);

                      // Update byReactionUserIds: remove current user from previous reaction bucket
                      try {
                        const userId = currentUserId;
                        if (userId && comment.myReaction) {
                          const current = { ...(updatedComment.byReactionUserIds || {}) } as Record<string, string[]>;
                          const prevKey = comment.myReaction;
                          const prevArr = Array.isArray(current[prevKey]) ? current[prevKey] : [];
                          current[prevKey] = prevArr.filter((id: string) => id !== userId);
                          updatedComment.byReactionUserIds = current;
                        }
                      } catch { }
                    } else {
                      updatedComment.myReaction = reactionName || undefined;
                      if (!comment.myReaction) {
                        updatedComment.reactionsCount = updatedComment.reactionsCount + 1;
                      }

                      // Update byReactionUserIds: add current user to new bucket and remove from previous if switched
                      try {
                        const userId = currentUserId;
                        if (userId && reactionName) {
                          const current = { ...(updatedComment.byReactionUserIds || {}) } as Record<string, string[]>;
                          // Remove from previous reaction bucket if switching
                          if (comment.myReaction && comment.myReaction !== reactionName) {
                            const prevArr = Array.isArray(current[comment.myReaction]) ? current[comment.myReaction] : [];
                            current[comment.myReaction] = prevArr.filter((id: string) => id !== userId);
                          }
                          // Add to new reaction bucket (dedupe)
                          const newArr = Array.isArray(current[reactionName]) ? current[reactionName] : [];
                          if (!newArr.includes(userId)) {
                            current[reactionName] = [...newArr, userId];
                          } else {
                            current[reactionName] = newArr;
                          }
                          updatedComment.byReactionUserIds = current;
                        }
                      } catch { }
                    }

                    return updatedComment;
                  }

                  // Case 2: reacting to a child reply
                  const hasMatchingChild = Array.isArray(comment.children) && comment.children.some((child) => child.uuid === commentId);
                  if (hasMatchingChild) {
                    const updatedParent = { ...comment } as GroupComment;
                    updatedParent.children = (comment.children || []).map((child) => {
                      if (child.uuid !== commentId) return child;

                      const updatedChild = { ...child } as GroupComment;
                      if (isRemoving) {
                        updatedChild.myReaction = undefined;
                        updatedChild.reactionsCount = Math.max(0, (updatedChild.reactionsCount || 0) - 1);
                        try {
                          const userId = currentUserId;
                          const prevKey = child.myReaction;
                          if (userId && prevKey) {
                            const current = { ...(updatedChild.byReactionUserIds || {}) } as Record<string, string[]>;
                            const prevArr = Array.isArray(current[prevKey]) ? current[prevKey] : [];
                            current[prevKey] = prevArr.filter((id: string) => id !== userId);
                            updatedChild.byReactionUserIds = current;
                          }
                        } catch { }
                      } else {
                        updatedChild.myReaction = reactionName || undefined;
                        if (!child.myReaction) {
                          updatedChild.reactionsCount = (updatedChild.reactionsCount || 0) + 1;
                        }
                        try {
                          const userId = currentUserId;
                          if (userId && reactionName) {
                            const current = { ...(updatedChild.byReactionUserIds || {}) } as Record<string, string[]>;
                            if (child.myReaction && child.myReaction !== reactionName) {
                              const prevArr = Array.isArray(current[child.myReaction]) ? current[child.myReaction] : [];
                              current[child.myReaction] = prevArr.filter((id: string) => id !== userId);
                            }
                            const newArr = Array.isArray(current[reactionName]) ? current[reactionName] : [];
                            if (!newArr.includes(userId)) {
                              current[reactionName] = [...newArr, userId];
                            } else {
                              current[reactionName] = newArr;
                            }
                            updatedChild.byReactionUserIds = current;
                          }
                        } catch { }
                      }

                      return updatedChild;
                    });
                    return updatedParent;
                  }

                  // No changes
                  return comment;
                }),
              })),
            };
          }
        );
      }

      return { previousData, commentsKey } as { previousData: any; commentsKey: any };
    },
    onSuccess: (_, { commentId, reactionName, isRemoving }) => {
      // Invalidate reactions for this comment
      queryClient.invalidateQueries({
        queryKey: COMMENTS_KEYS.reactions(commentId)
      });
    },
    onError: (error, variables, context) => {
      // Revert optimistic update on error
      if (context?.previousData && context?.commentsKey) {
        queryClient.setQueryData(context.commentsKey, context.previousData);
      }
      console.error('Failed to react to comment:', error);
    },
  });
};

/**
 * Hook to report a comment
 */
export const useReportCommentMutation = () => {
    return useMutation({
        mutationFn: ({ commentId, reason, additionalInfo }: {
            commentId: string;
            reason: string;
            additionalInfo?: string;
        }) =>
            commentsService.reportComment(commentId, reason, additionalInfo),
        onSuccess: (_, { commentId, reason }) => {
            // Track analytics
            analyticsService.trackEvent(AnalyticsEvent.REPORT_COMMENT, {
              commentId,
              reason,
            });
        },
        onError: (error) => {
            console.error('Failed to report comment:', error);
        },
    });
};

/**
 * Hook to toggle pin status of a comment
 */
export const useTogglePinCommentMutation = (
    commentType: CommentType = CommentType.GroupComment,
    groupId?: string,
    buildingId?: string
) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ commentId, isPinned }: { commentId: string; isPinned: boolean }) =>
            commentsService.togglePinComment(commentId, isPinned, commentType),
        onSuccess: (_, { commentId, isPinned }) => {
            // Invalidate both comments list and pinned comments
            queryClient.invalidateQueries({
                queryKey: COMMENTS_KEYS.list(groupId, undefined, buildingId)
            });
            queryClient.invalidateQueries({
                queryKey: COMMENTS_KEYS.pinned(groupId, buildingId)
            });

            // Track analytics
            analyticsService.trackEvent(AnalyticsEvent.PIN_COMMENT, {
              commentId,
              isPinned,
              commentType,
              groupId,
            });
        },
        onError: (error) => {
            console.error('Failed to toggle pin comment:', error);
        },
    });
};

/**
 * Hook to flag a comment
 */
export const useFlagCommentMutation = (
    commentType: CommentType = CommentType.GroupComment
) => {
    return useMutation({
        mutationFn: ({ commentId }: { commentId: string }) =>
            commentsService.flagComment(commentId, commentType),
        onSuccess: (_, { commentId }) => {
            // Track analytics
            analyticsService.trackEvent(AnalyticsEvent.FLAG_COMMENT, {
              commentId,
              commentType,
            });
        },
        onError: (error) => {
            console.error('Failed to flag comment:', error);
        },
    });
};

/**
 * Utility hook for invalidating comment queries
 */
export const useInvalidateComments = () => {
    const queryClient = useQueryClient();

    return (
        groupId?: string,
        groupEventId?: string,
        buildingId?: string
    ) => {
        queryClient.invalidateQueries({
            queryKey: COMMENTS_KEYS.list(groupId, groupEventId, buildingId)
        });
        if (groupId || buildingId) {
            queryClient.invalidateQueries({
                queryKey: COMMENTS_KEYS.pinned(groupId, buildingId)
            });
        }
    }
}; 